import { create } from "zustand";
import {
	createJSONStorage,
	persist,
	type StateStorage,
} from "zustand/middleware";
import type { ColorScheme } from "@/features/schedules/utils/colors.ts";
import type { TimeScale } from "@/features/schedules/utils/scale.ts";

interface CronogramaViewState {
	scale: TimeScale;
	scheme: ColorScheme;
	legend: boolean;
	setScale: (scale: TimeScale) => void;
	setScheme: (scheme: ColorScheme) => void;
	setLegend: (legend: boolean) => void;
}

function memoryStorage(): StateStorage {
	const values = new Map<string, string>();
	return {
		getItem: (name) => values.get(name) ?? null,
		setItem: (name, value) => {
			values.set(name, value);
		},
		removeItem: (name) => {
			values.delete(name);
		},
	};
}

export function safeLocalStorage(): StateStorage {
	const fallback = memoryStorage();
	return {
		getItem: (name) => {
			try {
				return window.localStorage.getItem(name);
			} catch {
				return fallback.getItem(name);
			}
		},
		setItem: (name, value) => {
			try {
				window.localStorage.setItem(name, value);
			} catch {
				fallback.setItem(name, value);
			}
		},
		removeItem: (name) => {
			try {
				window.localStorage.removeItem(name);
			} catch {
				fallback.removeItem(name);
			}
		},
	};
}

export const useCronogramaView = create<CronogramaViewState>()(
	persist(
		(set) => ({
			scale: "days",
			scheme: "status",
			legend: true,
			setScale: (scale) => set({ scale }),
			setScheme: (scheme) => set({ scheme }),
			setLegend: (legend) => set({ legend }),
		}),
		{
			name: "otd-cronograma-view",
			storage: createJSONStorage(safeLocalStorage),
			partialize: (state) => ({
				scale: state.scale,
				scheme: state.scheme,
				legend: state.legend,
			}),
		},
	),
);
